import { ConfigProvider, Segmented, type ThemeConfig } from 'antd';
import { ArticleIcon, GraduationCapIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AGGREGATED_DOCUMENT_NAME } from '../constants';
import { ScrollableRow } from '../../ScrollableRow';
import { colors, spacing, typography } from '../../../theme-v2';

export interface DocumentSwitcherDocument {
  id: string;
  name?: string;
}

export interface DocumentSwitcherProps {
  /** All documents in the experience. Renders nothing with 0–1 of them. */
  documents: DocumentSwitcherDocument[];
  /** Id of the currently displayed document. */
  activeDocumentId?: string;
  /** Fired when a document segment is chosen. */
  onSelectDocument: (id: string) => void;
  className?: string;
}

/**
 * Segmented control that switches the document shown in the experience page.
 * The aggregated document is labelled "All documents"; every other one by its
 * name. Overflowing segments scroll with the same arrows as the Knowledge Hub
 * tabs.
 */
export const DocumentSwitcher = ({
  documents,
  activeDocumentId,
  onSelectDocument,
  className,
}: DocumentSwitcherProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  if (documents.length <= 1) return null;

  const options = documents.map((doc) => {
    const isAggregated = doc.name === AGGREGATED_DOCUMENT_NAME;
    return {
      value: doc.id,
      label: isAggregated
        ? t('experienceV2.document.allDocuments')
        : (doc.name ?? doc.id),
      icon: isAggregated ? (
        <GraduationCapIcon size={14} weight="fill" />
      ) : (
        <ArticleIcon size={14} weight="fill" />
      ),
    };
  });

  return (
    <ConfigProvider theme={SEGMENTED_THEME}>
      <StyledRow
        className={className}
        activeKey={activeDocumentId}
        activeSelector=".ant-segmented-item-selected"
        // Blends the fades into the Segmented track.
        fadeColor={TRACK_BG}
      >
        <Segmented
          options={options}
          value={activeDocumentId}
          onChange={(value) => onSelectDocument(String(value))}
        />
      </StyledRow>
    </ConfigProvider>
  );
};

const TRACK_BG = colors.surface.muted;
const TRACK_RADIUS = 14;

// Mock styles through AntD's own Segmented tokens.
const SEGMENTED_THEME: ThemeConfig = {
  components: {
    Segmented: {
      trackBg: TRACK_BG,
      trackPadding: spacing.xs,
      itemSelectedBg: colors.surface.base,
      itemSelectedColor: colors.text.primary,
      itemColor: colors.text.tertiary,
      borderRadius: TRACK_RADIUS,
      borderRadiusSM: 10,
      fontSize: 13,
      // Item height = controlHeight - 2 × trackPadding.
      controlHeight: 40,
      // AntD subtracts lineWidth, so this yields 16px side padding.
      controlPaddingHorizontal: spacing.base + 1,
      // AntD spaces the icon from the label by marginSM / 2 → 7px.
      marginSM: 14,
    },
  },
};

// ─── Styled components ───

// Clips the fades to the track's rounded corners. Gap and weight have no
// Segmented token.
const StyledRow = styled(ScrollableRow)`
  align-self: center;
  max-width: 100%;
  border-radius: ${TRACK_RADIUS}px;
  overflow: hidden;

  .ant-segmented-group {
    gap: 2px;
  }

  .ant-segmented-item {
    font-weight: ${typography.weight.medium};
  }

  /* Centers the icon on the title instead of the text baseline. */
  .ant-segmented-item-label,
  .ant-segmented-item-icon {
    display: flex;
    align-items: center;
  }
`;
